/** RFC 0345 3c: the projects a session works in besides its home project.
 * Attaching grants the session's agent key on that project's group; detaching
 * revokes it. Only the session's owner sees this: the list lives in their
 * private World, and the grants are signed by their device. */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import type { WorldDoc } from '@mod/core/model'
import { useState } from 'react'
import { useSharingAccount } from '../../services/sharing/accountContext'
import { useTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { useDocument } from '../../services/world/repo/useDocument'

const bare = (id: string) => id.replace(/^automerge:/, '')

export function SessionProjects({ sessionId }: { sessionId: string }) {
  const account = useSharingAccount()
  const { sharing } = useTabRepoBoot()
  const [world] = useDocument<WorldDoc>(
    account?.opened.location.worldId as AnyDocumentId | undefined,
  )
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')

  const entry = world?.privateSessions?.[sessionId]
  if (!account || !sharing || !world || !entry) return null
  const home = bare(String(entry.projectId))
  const attached = (entry.attachedProjectIds ?? []).map((id) =>
    bare(String(id)),
  )
  const nameOf = (id: string) =>
    String(world.workspaces?.[`automerge:${id}`]?.displayName ?? 'Project')
  const choices = Object.keys(world.workspaces ?? {})
    .map(bare)
    .filter(
      (id) =>
        id !== home &&
        !attached.includes(id) &&
        id !== bare(String(world.sharedDocumentsProjectId ?? '')),
    )

  const run = async (projectId: string, attach: boolean) => {
    setBusy(projectId)
    setError('')
    try {
      const core = await import('@mod/core/sharing')
      const owner = {
        person: account.opened.location.personId,
        worldId: account.opened.location.worldId,
      }
      if (attach)
        await core.attachProjectToSession(sharing, owner, sessionId, projectId)
      // An incomplete detach throws and stays pending: the project stays
      // listed until a retry revokes every grant.
      else
        await core.detachProjectFromSession(
          sharing,
          owner,
          sessionId,
          projectId,
        )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change projects')
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="space-y-2 border-border border-t pt-2">
      <p className="font-medium text-xs">Projects for this session</p>
      <p className="text-muted-foreground text-xs">
        The agent can work in attached projects. Session members can see their
        code on its VM, but not their other pages.
      </p>
      {attached.map((id) => (
        <div key={id} className="flex items-center justify-between gap-2">
          <span className="truncate">{nameOf(id)}</span>
          <button
            type="button"
            disabled={!!busy}
            onClick={() => void run(id, false)}
            className="rounded px-1 text-muted-foreground text-xs hover:bg-muted hover:text-foreground"
          >
            {busy === id ? '…' : 'Detach'}
          </button>
        </div>
      ))}
      {choices.length > 0 ? (
        <select
          aria-label="Attach a project"
          value=""
          disabled={!!busy}
          onChange={(e) => e.target.value && void run(e.target.value, true)}
          className="w-full rounded border border-border bg-transparent px-1 py-0.5 text-xs"
        >
          <option value="">Attach a project…</option>
          {choices.map((id) => (
            <option key={id} value={id}>
              {nameOf(id)}
            </option>
          ))}
        </select>
      ) : (
        <p className="text-muted-foreground text-xs">
          No other projects available.
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs">
          {error}
        </p>
      )}
    </div>
  )
}
